import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, ClipboardList, Users, CheckSquare } from 'lucide-react';
import { useSearchStore } from '../../stores/searchStore';

export default function SearchModal() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = useSearchStore((s) => s.open);
  const query = useSearchStore((s) => s.query);
  const results = useSearchStore((s) => s.results);
  const loading = useSearchStore((s) => s.loading);
  const setQuery = useSearchStore((s) => s.setQuery);
  const search = useSearchStore((s) => s.search);
  const setOpen = useSearchStore((s) => s.setOpen);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, setOpen]);

  // Debounced search
  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        search();
      }, 300);
    },
    [setQuery, search]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleResultClick = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  if (!open) return null;

  const hasResults =
    results &&
    (results.jobs.length > 0 || results.agents.length > 0 || results.tasks.length > 0);
  const hasQuery = query.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg rounded-xl border border-border-subtle bg-bg-secondary shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
          <Search className="w-4 h-4 text-text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search jobs, agents, tasks..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
          />
          {loading && <Loader2 className="w-4 h-4 text-text-muted animate-spin flex-shrink-0" />}
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-text-muted bg-bg-tertiary border border-border-subtle rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {!hasQuery && (
            <div className="px-4 py-8 text-center text-xs text-text-muted">
              Start typing to search across jobs, agents, and tasks.
            </div>
          )}

          {hasQuery && !loading && !hasResults && (
            <div className="px-4 py-8 text-center text-xs text-text-muted">
              No results found for "{query}"
            </div>
          )}

          {results && results.jobs.length > 0 && (
            <div className="px-2 py-2">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                Jobs
              </div>
              {results.jobs.map((job) => (
                <button
                  key={job.id}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-bg-tertiary transition-colors"
                  onClick={() => handleResultClick('/jobs')}
                >
                  <ClipboardList className="w-4 h-4 text-accent-purple flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-text-primary truncate">{job.name}</div>
                    <div className="text-[11px] text-text-muted">{job.schedule_type}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results && results.agents.length > 0 && (
            <div className="px-2 py-2">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                Agents
              </div>
              {results.agents.map((agent) => (
                <button
                  key={agent.slug}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-bg-tertiary transition-colors"
                  onClick={() => handleResultClick('/agents')}
                >
                  <Users className="w-4 h-4 text-avatar-cyan flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-text-primary truncate">{agent.name}</div>
                    <div className="text-[11px] text-text-muted">
                      {agent.title} &middot; {agent.department}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results && results.tasks.length > 0 && (
            <div className="px-2 py-2">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                Tasks
              </div>
              {results.tasks.map((task) => (
                <button
                  key={task.id}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-bg-tertiary transition-colors"
                  onClick={() => handleResultClick('/tasks')}
                >
                  <CheckSquare className="w-4 h-4 text-success flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-text-primary truncate">{task.title}</div>
                    <div className="text-[11px] text-text-muted">{task.status}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
